import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../auth/AuthProvider'
import { useUIStore } from '../store/uiStore'
import { usePrivacyStore } from '../store/privacyStore'
import { modules } from '../modules/registry'
import { navSections } from './navConfig'

function userInitials(name: string | undefined): string {
  if (!name) return '?'
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export default function Header() {
  const { pathname } = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuth()
  const { darkMode, toggleDarkMode } = useUIStore()
  const privacyMode = usePrivacyStore((s) => s.privacyMode)
  const togglePrivacy = usePrivacyStore((s) => s.toggle)
  const { t } = useTranslation('shell')
  // Prefer the derived display name ("Ömer Beispiel"); UPN/email is a last resort.
  const displayName = user?.displayName ?? user?.userDetails

  // Cmd/Ctrl+Shift+B — quick toggle for privacy screen ("Blur")
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === 'b') {
        e.preventDefault()
        togglePrivacy()
      }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [togglePrivacy])

  const currentModule = modules.find((m) => m.path === pathname)
  // Pages that share one module (QM setup) have no own registry entry: take the nav label.
  const navLabel = navSections.flatMap((s) => s.items).find((i) => i.path === pathname)?.label
  const pageTitle = currentModule?.label ?? navLabel ?? 'Dashboard'

  const toggleMobileSidebar = () => {
    window.dispatchEvent(new CustomEvent('mobile-sidebar-toggle'))
  }

  return (
    <header className="privacy-exempt liquid-glass rounded-3xl flex justify-between items-center px-4 md:px-8 h-16 md:h-14 mt-4 mx-4 z-40 flex-shrink-0 transition-all">
      {/* Left side: Hamburger + Page Title */}
      <div className="flex items-center gap-3">
        {/* Hamburger Menu (Mobile Only) */}
        <button 
          onClick={toggleMobileSidebar}
          className="lg:hidden p-2 -ml-2 text-outline-variant hover:text-on-surface hover:bg-surface-container rounded-lg transition-colors"
          aria-label="Menü öffnen"
        >
          <span className="material-symbols-outlined">menu</span>
        </button>

        <h2 className="font-headline font-bold text-sm md:text-base text-on-surface leading-tight truncate max-w-[140px] md:max-w-none">
          {pageTitle}
        </h2>
      </div>

      {/* Controls */}
      <div className="flex items-center gap-1 md:gap-3">
        {/* Command palette trigger — opens the glass ⌘K sheet */}
        <button
          type="button"
          onClick={() => window.dispatchEvent(new Event('mc-cmdk-open'))}
          title="Suchen (⌘K)"
          className="hidden sm:flex items-center gap-2 liquid-glass-pill px-3 py-1.5 rounded-lg text-on-surface-variant hover:text-on-surface transition-colors"
        >
          <span className="material-symbols-outlined" style={{ fontSize: 15 }}>search</span>
          <span className="text-sm w-20 md:w-36 text-left truncate">{t('header.search')}</span>
          <kbd className="ml-1 font-mono text-[10px] tracking-[0.1em] uppercase border border-white/40 rounded px-1 py-px leading-none">⌘K</kbd>
        </button>

        {/* Privacy-Modus — blurs every <Sensitive> on screen. Cmd/Ctrl+Shift+B. */}
        <button
          type="button"
          onClick={togglePrivacy}
          aria-pressed={privacyMode}
          title={`${privacyMode ? 'Privat-Modus aus' : 'Privat-Modus an'} (⌘⇧B)`}
          className={`flex items-center gap-1 p-2 rounded-lg transition-colors text-xs font-medium ${
            privacyMode
              ? 'text-primary bg-primary/10 hover:bg-primary/15'
              : 'text-outline-variant hover:bg-surface-container'
          }`}
        >
          <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
            {privacyMode ? 'visibility_off' : 'visibility'}
          </span>
          <span className="hidden md:inline uppercase tracking-wide">{privacyMode ? 'PRIV' : ''}</span>
        </button>

        {/* Language switcher removed — language is configured in /settings. */}


        {/* User menu - Always visible circle */}
        <div className="flex items-center gap-2 cursor-pointer hover:bg-surface-container rounded-lg px-2 py-1.5 transition-colors group relative">
          <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center border border-primary/20 shadow-sm">
            <span className="text-primary text-xs font-bold">{userInitials(displayName)}</span>
          </div>
          <span className="hidden sm:inline text-xs font-semibold text-on-surface max-w-[140px] truncate">
            {displayName ?? t('auth.login')}
          </span>
          <span className="hidden sm:inline material-symbols-outlined text-outline-variant !text-[16px]">
            expand_more
          </span>
          
          {/* Dropdown */}
          <div className="absolute right-0 top-full pt-1 w-48 hidden group-hover:block z-50">
            <div className="bg-surface-container-lowest border border-outline-variant/30 rounded-xl shadow-lg py-1.5 overflow-hidden">
              <div className="px-3 py-2 border-b border-outline-variant/30 mb-1 sm:hidden">
                <p className="text-xs font-bold text-on-surface truncate">{displayName}</p>
              </div>
              <button
                onClick={() => navigate('/settings')}
                className="w-full text-left px-3 py-2.5 text-sm text-on-surface hover:bg-surface-container-low flex items-center gap-3 transition-colors"
              >
                <span className="material-symbols-outlined text-outline-variant" style={{ fontSize: 18 }}>
                  settings
                </span>
                Einstellungen
                <span className="ml-auto material-symbols-outlined text-outline-variant !text-[14px]">arrow_forward</span>
              </button>
              <button
                onClick={toggleDarkMode}
                className="w-full text-left px-3 py-2.5 text-sm text-on-surface hover:bg-surface-container-low flex items-center gap-3 transition-colors"
              >
                <span className="material-symbols-outlined text-outline-variant" style={{ fontSize: 18 }}>
                  {darkMode ? 'light_mode' : 'dark_mode'}
                </span>
                {darkMode ? t('header.lightMode') : t('header.darkMode')}
              </button>
              <div className="h-px bg-outline-variant/30 mx-2 my-1" />
              <button
                onClick={logout}
                className="w-full text-left px-3 py-2.5 text-sm text-error hover:bg-error/5 flex items-center gap-3 transition-colors"
              >
                <span className="material-symbols-outlined" style={{ fontSize: 18 }}>
                  logout
                </span>
                {t('header.logout')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </header>
  )
}
